import React, { useState } from 'react';
import mapboxgl from 'mapbox-gl';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { format } from 'date-fns';
import { Project } from '../types/project';
import { useRestoreProject, usePermanentlyDeleteProject } from '../api/projects';
import { useAuth } from '../contexts/AuthContext';
import { isViewOnly } from '../utils/permissions';
import ConfirmDeleteModal from './ConfirmDeleteModal';

mapboxgl.accessToken = "pk.eyJ1IjoiYnVwcG9vODUiLCJhIjoiY2xzaGV6M2d6MjV4ajJudDAwOXo1bjMwcSJ9.0LeBUIZbR4UWWloslEZjlA";

type ArchivedProjectCardProps = {
  project: Project;
};

const ArchivedProjectCard: React.FC<ArchivedProjectCardProps> = ({ project }) => {
  const { mutate: restoreProject } = useRestoreProject();
  const { mutateAsync: permanentlyDelete, isPending: isSubmitting } = usePermanentlyDeleteProject();
  const { user } = useAuth();

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Backend marks the project "deleting" and removes it in the background; the
  // card stays visible (showing progress, actions disabled) until it's gone.
  const isDeleting = project.status === 'deleting';

  const handleConfirmDelete = async () => {
    try {
      await permanentlyDelete(String(project.id));
    } catch {
      // usePermanentlyDeleteProject logs the error in its onError handler.
    } finally {
      setConfirmOpen(false);
    }
  };

  // Restore is available to all non-view-only users (reversible). Permanent
  // delete stays gated behind the 'delete_project' capability.
  const viewOnly = isViewOnly(user);
  const canRestore = !viewOnly;
  const hasDeleteCapability = user?.capabilities?.some(
    (capability) => capability.codename === 'delete_project'
  );
  const canDelete = !viewOnly && (user?.is_staff || hasDeleteCapability);

  return (
    <Card sx={{ width: 360, height: 360, position: 'relative', opacity: 0.7, filter: 'grayscale(0.4)' }}>
      <CardContent style={{ height: '100%' }}>
        <div style={{ height: 200, width: '100%', marginBottom: 8 }}>
          <img
            src={`${project.map_screenshot_url}?access_token=${mapboxgl.accessToken}`}
            alt="Map screenshot"
            height={200}
            width={336}
            style={{ filter: 'grayscale(1)', opacity: 0.6 }}
          />
        </div>
        <Typography
          noWrap
          component="div"
          sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: 600, color: 'text.secondary' }}
        >
          {project.name}
        </Typography>
        {isDeleting ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CircularProgress size={12} sx={{ color: '#ef4444' }} />
            <Typography variant="body2" sx={{ color: '#ef4444' }}>
              Deleting…
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Archived · Updated: {format(new Date(project.updated), 'MMM d, yyyy')}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          By: {project.username}
        </Typography>
      </CardContent>
      <Box
        sx={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          display: 'flex',
          gap: 0.5,
        }}
      >
        {canRestore && !isDeleting && (
          <Tooltip title="Restore project">
            <IconButton
              size="small"
              onClick={() => restoreProject(String(project.id))}
              sx={{ color: '#00d4ad' }}
            >
              <UnarchiveOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && !isDeleting && (
          <Tooltip title="Delete permanently">
            <IconButton
              size="small"
              onClick={() => setConfirmOpen(true)}
              sx={{ color: '#ef4444' }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isSubmitting}
        title="Permanently delete project"
        message={
          <>
            <Typography variant="body1" gutterBottom>
              Permanently delete "{project.name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This removes the project and all of its data — simulations, assets,
              uploaded files and results — from storage.
            </Typography>
          </>
        }
        warningText="This action cannot be undone."
        confirmLabel="Delete permanently"
      />
    </Card>
  );
};

export default ArchivedProjectCard;
